"use client";
import React from "react";
import { twMerge } from "tailwind-merge";
import { controlStyles, type Size } from "../../utils/controlStyles";
import { Icon } from "../Icon/Icon";

export interface ChipProps {
  /** The content to display inside the chip */
  children: React.ReactNode;
  /** Callback when the remove button is clicked */
  onRemove?: () => void;
  /** Size variant of the chip */
  size?: Size | "xs";
  /** Visual variant of the chip */
  variant?: "default" | "brand" | "secondary" | "info";
  /** Whether the chip can be removed (shows X button) */
  isRemovable?: boolean;
  /** Whether the chip is disabled */
  isDisabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Click handler for the chip itself (not the remove button) */
  onClick?: () => void;
}

const chipVariantStyles = {
  default: "bg-background-muted text-action-default border border-border-muted hover:bg-background-hover",
  brand: "bg-action-brand/10 text-action-brand border border-action-brand/20 hover:bg-action-brand/15",
  secondary: "bg-background-input text-text-body border border-border-input hover:bg-background-hover",
  info: "bg-feedback-info-background text-feedback-info-text border border-feedback-info-border hover:bg-feedback-info-background/80",
} as const;

const chipSizeStyles = {
  xs: "px-1.5 py-[2px] gap-0.5",
  sm: "px-2 py-1 gap-1",
  md: "px-3 py-1.5 gap-1.5",
  lg: "px-3 py-2 gap-2",
  xl: "px-4 py-2.5 gap-2",
};

const disabledStyles = "opacity-75 cursor-not-allowed pointer-events-none";

function getChipStyles(props: {
  variant: NonNullable<ChipProps["variant"]>;
  size: NonNullable<ChipProps["size"]>;
  isDisabled?: boolean;
  isClickable?: boolean;
  className?: string;
}) {
  const { variant, size, isDisabled, isClickable, className } = props;

  // Get text size - xs uses text-xs, otherwise use controlStyles
  const textSize = size === "xs" ? "text-xs" : controlStyles[size].text;

  return twMerge(
    // Base styles
    "inline-flex items-center justify-center rounded-full font-medium transition-colors",
    "whitespace-nowrap select-none",

    // Size styles
    chipSizeStyles[size],
    textSize,

    // Variant styles
    chipVariantStyles[variant],

    // Interactive styles
    isClickable && !isDisabled && "cursor-pointer",

    // Disabled styles
    isDisabled && disabledStyles,

    // Custom className
    className
  );
}

/**
 * Chip
 *
 * A compact element that represents an input, attribute, or action.
 * Can be removable with an X button and supports different variants and sizes.
 */
export function Chip({
  children,
  onRemove,
  size = "md",
  variant = "default",
  isRemovable = true,
  isDisabled = false,
  className,
  onClick,
}: ChipProps) {
  const isClickable = !!onClick && !isDisabled;
  const showRemoveButton = isRemovable && onRemove && !isDisabled;

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onRemove && !isDisabled) {
      onRemove();
    }
  };

  const handleChipClick = () => {
    if (onClick && !isDisabled) {
      onClick();
    }
  };

  const iconSize = size === "xs" ? 10 : size === "sm" ? 12 : size === "md" ? 14 : 16;

  // Render remove control as span when chip is clickable (button), button when chip is static (span)
  const removeControl = showRemoveButton ? (
    isClickable ? (
      <span
        onClick={handleRemoveClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleRemoveClick(e as unknown as React.MouseEvent);
          }
        }}
        role="button"
        tabIndex={0}
        className="flex-shrink-0 rounded-full p-0.5 hover:bg-black/10 transition-colors cursor-pointer"
        aria-label="Remove"
      >
        <Icon name="X" size={iconSize} className="text-current opacity-70 hover:opacity-100" />
      </span>
    ) : (
      <button
        type="button"
        onClick={handleRemoveClick}
        className="flex-shrink-0 rounded-full p-0.5 hover:bg-black/10 transition-colors cursor-pointer"
        aria-label="Remove"
        tabIndex={-1}
      >
        <Icon name="X" size={iconSize} className="text-current opacity-70 hover:opacity-100" />
      </button>
    )
  ) : null;

  const chipContent = (
    <>
      <span className="truncate">{children}</span>
      {removeControl}
    </>
  );

  if (isClickable) {
    return (
      <button
        type="button"
        onClick={handleChipClick}
        className={getChipStyles({
          variant,
          size,
          isDisabled,
          isClickable,
          className,
        })}
        disabled={isDisabled}
      >
        {chipContent}
      </button>
    );
  }

  return (
    <span
      className={getChipStyles({
        variant,
        size,
        isDisabled,
        isClickable,
        className,
      })}
    >
      {chipContent}
    </span>
  );
}
